import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Camera, Upload, Wifi, WifiOff, Eye, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { readingSession } from "@/lib/session";
import { offlineReadingLines, offlineMarkings, COUPLES_COMPATIBILITY } from "@/lib/snippets";
import { PALMISTRY_FACTS } from "@/lib/lore";

const MODE_SHOTS = {
  dominant: [{ label: "Dominant Hand" }],
  both: [{ label: "Left Hand" }, { label: "Right Hand" }],
  couples: [{ label: "Seeker One" }, { label: "Seeker Two" }],
};

// strip data URL prefix -> raw base64
const stripPrefix = (dataUrl) => dataUrl.split(",")[1] || "";

export default function Capture() {
  const [params] = useSearchParams();
  const mode = params.get("mode") || "dominant";
  const isPortal = params.get("portal") === "1";
  const revealPath = isPortal ? "/reading/reveal?portal=1" : "/reading/reveal";
  const shots = MODE_SHOTS[mode] || MODE_SHOTS.dominant;
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState(false);
  const [captured, setCaptured] = useState([]);
  const [online, setOnline] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [factIdx, setFactIdx] = useState(() => Math.floor(Math.random() * PALMISTRY_FACTS.length));

  const idx = captured.length;
  const done = idx >= shots.length;
  const currentLabel = shots[Math.min(idx, shots.length - 1)]?.label;

  useEffect(() => {
    if (!processing) return;
    const id = setInterval(() => setFactIdx((i) => (i + 1) % PALMISTRY_FACTS.length), 3500);
    return () => clearInterval(id);
  }, [processing]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCamReady(true);
      } catch {
        setCamError(true);
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = () => {
    const video = videoRef.current;
    if (!video || done) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 960;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const b64 = stripPrefix(canvas.toDataURL("image/jpeg", 0.6));
    setCaptured((c) => [...c, { label: shots[c.length].label, b64 }]);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file || done) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCaptured((c) => [...c, { label: shots[c.length].label, b64: stripPrefix(reader.result) }]);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Both portal and practitioner readings go straight to the reveal — no payment.
  const proceed = (draft) => {
    readingSession.set(draft);
    navigate(revealPath, { replace: true });
  };

  const reveal = async () => {
    if (captured.length === 0) return;
    setProcessing(true);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());

    const handsInput = captured.map((c) => ({ label: c.label, image_b64: c.b64 }));
    if (online) {
      try {
        const res = await api.post("/readings/analyze", { mode, hands: handsInput });
        await proceed({
          mode,
          online: true,
          hands: res.hands,
          reading_lines: res.reading_lines,
          compatibility: res.compatibility,
          markings: res.markings,
          overall: res.overall,
        });
        return;
      } catch {
        toast("Reading offline — continuing with a guided reflection", { icon: "✦" });
      }
    }
    // Guided fallback: local snippets + stylized photo
    await proceed({
      mode,
      online: false,
      hands: captured.map((c) => ({ label: c.label, photo: c.b64 })),
      reading_lines: offlineReadingLines(),
      compatibility: mode === "couples" ? COUPLES_COMPATIBILITY : null,
      markings: offlineMarkings(),
      overall: "Every hand is an invitation to slow down and notice what wants attention.",
    });
  };

  return (
    <div className="relative flex min-h-screen flex-col bg-black">
      {/* Camera / preview area */}
      <div className="relative flex-1 overflow-hidden">
        {camReady && !camError ? (
          <video ref={videoRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-soma-surface px-8 text-center">
            <Camera size={44} className="text-soma-borderStrong" />
            <p className="font-display text-2xl text-soma-on">
              {camError ? "Camera unavailable" : "Preparing camera…"}
            </p>
            <p className="text-sm text-soma-muted">
              {camError
                ? "Grant camera access, or upload a photo of the palm — fingertips up, wrist at the bottom."
                : "Allow camera access to capture the palm."}
            </p>
          </div>
        )}

        {/* Guide frame */}
        {camReady && !camError && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-4">
            <div
              className="rounded-[120px] border-2 border-dashed border-soma-brand/70"
              style={{ width: "62%", aspectRatio: "0.72" }}
            />
            <div className="flex flex-col items-center gap-1.5">
              <p className="rounded-full bg-black/50 px-4 py-1.5 text-sm text-white">
                Place the {currentLabel?.toLowerCase()} within the frame
              </p>
              <p className="rounded-full bg-soma-brand/90 px-3 py-1 text-xs font-semibold text-soma-surface">
                Fingertips up · wrist at the bottom
              </p>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="capture-back"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-white"
          >
            <X size={20} />
          </button>
          <span className="rounded-full bg-black/55 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-soma-brand">
            {mode}
          </span>
          <button
            onClick={() => setOnline((o) => !o)}
            data-testid="ai-mode-toggle"
            className="flex items-center gap-2 rounded-full bg-black/55 px-4 py-2 text-xs font-bold"
            style={{ color: online ? "#D4B872" : "#8A938F" }}
          >
            {online ? <Wifi size={15} /> : <WifiOff size={15} />}
            {online ? "AI" : "Guided"}
          </button>
        </div>

        {/* Captured thumbnails */}
        {captured.length > 0 && (
          <div className="absolute bottom-4 left-4 z-10 flex gap-2">
            {captured.map((c, i) => (
              <img
                key={i}
                src={`data:image/jpeg;base64,${c.b64}`}
                alt=""
                className="h-12 w-12 rounded-lg border border-soma-brand object-cover"
              />
            ))}
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="border-t border-soma-border bg-soma-surface p-6">
        <p className="mb-1 text-center text-sm text-soma-on2">
          {done
            ? "All palms captured"
            : `Palm ${idx + 1} of ${shots.length} · ${currentLabel}`}
        </p>
        <p className="mb-4 text-center text-xs text-soma-muted">
          {done
            ? "Review, then reveal below"
            : "Hold the palm upright — fingertips at the top, wrist at the bottom"}
        </p>

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="capture-file-input" />

        {!done ? (
          <div className="flex items-center justify-center gap-6">
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="upload-button"
              className="flex h-14 w-14 items-center justify-center rounded-full bg-soma-surface3 text-soma-on2 hover:text-soma-brand"
            >
              <Upload size={22} />
            </button>
            <button
              onClick={snap}
              disabled={!camReady || camError}
              data-testid="shutter-button"
              className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-soma-brand bg-black/40 disabled:opacity-40"
            >
              <span className="h-14 w-14 rounded-full bg-soma-brand" />
            </button>
            <div className="h-14 w-14" />
          </div>
        ) : (
          <div className="mx-auto flex max-w-sm flex-col gap-3">
            <button
              onClick={reveal}
              disabled={processing}
              data-testid="reveal-button"
              className="flex items-center justify-center gap-2 rounded-xl bg-soma-brand py-4 font-semibold text-soma-surface hover:opacity-90 disabled:opacity-60"
            >
              <Eye size={18} />
              {processing ? "Tracing the lines…" : "Reveal the sacred print"}
            </button>
            <button
              onClick={() => setCaptured([])}
              data-testid="retake-button"
              className="flex items-center justify-center gap-2 rounded-xl border border-soma-border py-3 text-sm text-soma-on2"
            >
              <RotateCcw size={16} /> Retake
            </button>
          </div>
        )}
      </div>

      {processing && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6 bg-soma-surface/95">
          <div className="candle" aria-hidden="true">
            <span className="flame" />
            <span className="wick" />
            <span className="wax" />
          </div>
          <p className="font-display text-xl italic tracking-wide text-soma-brand">Tracing the lines…</p>
          <p className="max-w-sm px-8 text-center text-sm leading-relaxed text-soma-on2" data-testid="loading-fact">
            {PALMISTRY_FACTS[factIdx]}
          </p>
        </div>
      )}
    </div>
  );
}
